import type {
  User as SupabaseUser,
  Session as SupabaseSession,
  AuthError as SupabaseAuthError,
} from '@supabase/supabase-js'
import { useSentry, useMixpanel } from '@staffingengine/common'
import { createGlobalState } from '@vueuse/core'

const REDIRECT_URL = import.meta.env.VITE_SUPABASE_AUTH_REDIRECT_URL

export interface AuthUserMetadata {
  onereach_account_id?: string
  onereach_user_id?: string
  organization?: string
  user?: string
}

export const useAuth = createGlobalState(() => {
  const client = useDatabaseClient()

  //
  // Session
  //

  const session = ref<SupabaseSession | undefined>(undefined)
  const isSignedIn = ref(false)

  watchEffect(() => {
    const _session = session.value

    isSignedIn.value = !!_session
  })

  const getSession = async (): Promise<SupabaseSession | undefined> => {
    try {
      const { data, error } = await client.supabase.auth.getSession()

      if (error) throw error

      if (!data?.session) return undefined

      session.value = data.session
      user.value = data.session?.user

      if (!data.session?.user) {
        useSentry().setUser(null)
        useMixpanel().people.set({
          'Last Sign Out At': new Date(),
        })
        useMixpanel().reset()
      } else {
        useSentry().setUser({
          id: data.session.user.id,
          email: data.session.user.email,
          organization: data.session.user.user_metadata?.organization,
          onereachAccountId: data.session.user.user_metadata?.onereach_account_id,
          onereachUserId: data.session.user.user_metadata?.onereach_user_id,
        })
        useMixpanel().identify(data.session.user.id)
        useMixpanel().people.set({
          $email: data.session.user.email,
          'Last Sign In At': new Date(),
        })
        useMixpanel().people.set_once({
          'Auth Id': data.session.user.id,
          'First Sign In At': new Date(),
          'Invited On': data.session.user.invited_at,
        })
      }

      return session.value
    } catch (error) {
      const err = error as SupabaseAuthError
      console.error(err.message, err)
    }
  }

  //
  // User
  //

  const user = ref<SupabaseUser | undefined>(undefined)

  const userMetadata = ref<AuthUserMetadata | undefined>(undefined)
  const userId = ref<string | undefined>(undefined)
  const organizationId = ref<string | undefined>(undefined)

  watchEffect(() => {
    const _user = user.value

    userMetadata.value = _user?.user_metadata
    userId.value = (_user?.user_metadata as AuthUserMetadata)?.onereach_user_id
    organizationId.value = (_user?.user_metadata as AuthUserMetadata)?.onereach_account_id
  })

  //
  // Errors
  //

  const errorMessage = ref<string | undefined>(undefined)

  const clearError = () => {
    errorMessage.value = undefined
  }

  const processAuthError = (error: SupabaseAuthError) => {
    if (import.meta.env.DEV) {
      console.error(error.message, error)
    }

    if (error.message.includes('NetworkError')) {
      errorMessage.value = 'Unable to contact authentication server.'
      return
    }

    errorMessage.value = error.message
  }

  //
  // Functions
  //

  const reset = () => {
    errorMessage.value = undefined
    user.value = undefined
    session.value = undefined
  }

  const signIn = async (email: string, password: string) => {
    try {
      errorMessage.value = undefined

      if (!email) {
        errorMessage.value = 'Please provide an email'
        return undefined
      }

      if (!password) {
        errorMessage.value = 'Please provide a password'
        return undefined
      }

      const { data, error } = await client.supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) throw error

      if (!data.user) {
        throw new Error('user not returned')
      }

      session.value = data.session
      user.value = data.user

      return data
    } catch (error) {
      processAuthError(error as SupabaseAuthError)
      return undefined
    }
  }

  const signOut = async () => {
    try {
      errorMessage.value = undefined

      const { error } = await client.supabase.auth.signOut()

      if (error) throw error

      reset()

      return undefined
    } catch (error) {
      processAuthError(error as SupabaseAuthError)
      return undefined
    }
  }

  const forgotPassword = async (email: string) => {
    try {
      errorMessage.value = undefined

      if (!email) {
        errorMessage.value = 'Please provide an email'
        return undefined
      }

      const { data, error } = await client.supabase.auth.resetPasswordForEmail(email, {
        redirectTo: REDIRECT_URL,
      })

      if (error) throw error

      return data
    } catch (error) {
      processAuthError(error as SupabaseAuthError)
      return undefined
    }
  }

  const updatePassword = async (newPassword: string) => {
    try {
      errorMessage.value = undefined

      if (!newPassword) {
        errorMessage.value = 'Please provide a new password'
        return undefined
      }

      const { data, error } = await client.supabase.auth.updateUser({ password: newPassword })

      if (error) throw error

      if (!data.user) {
        throw new Error('user not returned')
      }

      user.value = data.user

      useMixpanel().people.set({
        'Password Last Updated At': new Date(),
      })

      return data
    } catch (error) {
      processAuthError(error as SupabaseAuthError)
      return undefined
    }
  }

  //
  // Lifecycle
  //
  const init = async () => {
    if (!session.value) {
      await getSession()
    }
  }

  return {
    session,
    user,
    errorMessage,

    // computed
    isSignedIn,
    userMetadata,
    organizationId,
    userId,

    // actions
    signIn,
    signOut,
    forgotPassword,
    updatePassword,
    init,
    reset,
    clearError,
  }
})
